import React, { useEffect, useState, useCallback } from "react";
import { Table, Tag, Button, Tabs, Badge, Input, Select, Dropdown, Menu } from "antd";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import moment from "moment";
import { IoMdAdd } from "react-icons/io";
import {
  RiArrowDownSLine,
  RiEyeLine,
  RiEditLine,
  RiToggleLine,
  RiCheckLine,
  RiCloseLine,
  RiCalendarEventLine,
} from "react-icons/ri";
import {
  getAllHmos,
  getAllHmoCustomers,
  createHmo,
  updateHmo,
  toggleHmoStatus,
  getPendingHmoApprovals,
  approveHmoUser,
  rejectHmoUser,
  extendHmoExpiry,
} from "../../../redux/actions";
import { dashboardRoute } from "../../../constants";
import ModalCreateOrEditHmo from "./ModalCreateOrEditHmo";
import ModalApproveHmo from "./ModalApproveHmo";
import ModalRejectHmo from "./ModalRejectHmo";
import ModalExtendExpiry from "./ModalExtendExpiry";
import ModalHmoCustomers from "./ModalHmoCustomers";
import Container from "./style";

const { TabPane } = Tabs;
const { Search } = Input;
const { Option } = Select;

const ManageHmo = () => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("hmos");

  // HMOs state
  const [hmos, setHmos] = useState([]);
  const [hmoLoading, setHmoLoading] = useState(false);
  const [selectedHmo, setSelectedHmo] = useState(null);
  const [showHmoModal, setShowHmoModal] = useState(false);
  const [hmoModalLoading, setHmoModalLoading] = useState(false);

  // Customers modal state
  const [viewCustomerHmo, setViewCustomerHmo] = useState(null);

  // Pending Approvals state
  const [pendingUsers, setPendingUsers] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [pendingPagination, setPendingPagination] = useState({ current: 1, pageSize: 15, total: 0 });
  const [pendingSearch, setPendingSearch] = useState("");

  // Approval / Rejection modal state
  const [userToApprove, setUserToApprove] = useState(null);
  const [approveLoading, setApproveLoading] = useState(false);
  const [userToReject, setUserToReject] = useState(null);
  const [rejectLoading, setRejectLoading] = useState(false);

  // Extend expiry modal state
  const [userToExtend, setUserToExtend] = useState(null);
  const [extendLoading, setExtendLoading] = useState(false);

  // All HMO Customers tab state
  const [allCustomers, setAllCustomers] = useState([]);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [customerPagination, setCustomerPagination] = useState({ current: 1, pageSize: 15, total: 0 });
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerHmoFilter, setCustomerHmoFilter] = useState("");
  const [customerStatusFilter, setCustomerStatusFilter] = useState("");

  // Load HMO providers
  const loadHmos = useCallback(async () => {
    setHmoLoading(true);
    const data = await dispatch(getAllHmos());
    setHmos(data || []);
    setHmoLoading(false);
  }, [dispatch]);

  // Helper to extract list and pagination info from various response structures
  const parsePaginatedData = (res) => {
    let list = [];
    let current = 1;
    let pageSize = pendingPagination.pageSize || 15;
    let total = 0;

    if (!res) return { list, current, pageSize, total };

    // Structure 1: { status: 'success', data: { data: [...], current_page: 1, total: 10 } }
    if (res.data && Array.isArray(res.data.data)) {
      list = res.data.data;
      current = res.data.current_page || 1;
      pageSize = res.data.per_page || pageSize;
      total = res.data.total !== undefined ? res.data.total : list.length;
    }
    // Structure 2: { data: [...], current_page: 1, total: 10 }
    else if (Array.isArray(res.data)) {
      list = res.data;
      current = res.current_page || 1;
      pageSize = res.per_page || pageSize;
      total = res.total !== undefined ? res.total : list.length;
    }
    // Structure 3: Direct array [...]
    else if (Array.isArray(res)) {
      list = res;
      total = res.length;
    }

    return { list, current, pageSize, total };
  };

  // Load Pending Approvals
  const loadPending = useCallback(async (page = 1, search = pendingSearch) => {
    setPendingLoading(true);
    let q = `page=${page}&perPage=${pendingPagination.pageSize}`;
    if (search) q += `&search=${encodeURIComponent(search)}`;
    const res = await dispatch(getPendingHmoApprovals(q));
    setPendingLoading(false);
    
    const { list, current, pageSize, total } = parsePaginatedData(res);
    setPendingUsers(list);
    setPendingPagination({
      current,
      pageSize,
      total,
    });
  }, [dispatch, pendingSearch, pendingPagination.pageSize]);

  // Load All HMO Customers
  const loadAllCustomers = useCallback(async (page = 1, search = customerSearch, hmoId = customerHmoFilter, status = customerStatusFilter) => {
    setCustomerLoading(true);
    let q = `page=${page}&perPage=${customerPagination.pageSize}`;
    if (search) q += `&search=${encodeURIComponent(search)}`;
    if (hmoId) q += `&hmo_id=${hmoId}`;
    if (status) q += `&status=${status}`;

    const res = await dispatch(getAllHmoCustomers(q));
    setCustomerLoading(false);
    
    const { list, current, pageSize, total } = parsePaginatedData(res);
    setAllCustomers(list);
    setCustomerPagination({
      current,
      pageSize,
      total,
    });
  }, [dispatch, customerSearch, customerHmoFilter, customerStatusFilter, customerPagination.pageSize]);

  useEffect(() => {
    loadHmos();
    loadPending(1, "");
    loadAllCustomers(1);
  }, [loadHmos, loadPending, loadAllCustomers]);

  // Handle HMO Create / Update
  const handleHmoSubmit = async (values) => {
    setHmoModalLoading(true);
    let res;
    if (selectedHmo) {
      res = await dispatch(updateHmo(selectedHmo.id, values));
    } else {
      res = await dispatch(createHmo(values));
    }
    setHmoModalLoading(false);
    if (res && res.success) {
      setShowHmoModal(false);
      setSelectedHmo(null);
      loadHmos();
    }
  };

  // Handle Toggle HMO status
  const handleToggleStatus = async (hmo) => {
    const res = await dispatch(toggleHmoStatus(hmo.id));
    if (res && res.success) {
      loadHmos();
    }
  };

  // Handle Approve
  const handleApproveSubmit = async (values) => {
    if (!userToApprove) return;
    setApproveLoading(true);
    const res = await dispatch(approveHmoUser(userToApprove.id, values));
    setApproveLoading(false);
    if (res && res.success) {
      setUserToApprove(null);
      loadPending(pendingPagination.current);
      loadHmos();
      loadAllCustomers(customerPagination.current);
    }
  };

  // Handle Reject
  const handleRejectSubmit = async (values) => {
    if (!userToReject) return;
    setRejectLoading(true);
    const res = await dispatch(rejectHmoUser(userToReject.id, values));
    setRejectLoading(false);
    if (res && res.success) {
      setUserToReject(null);
      loadPending(pendingPagination.current);
      loadHmos();
      loadAllCustomers(customerPagination.current);
    }
  };

  // Handle Extend Expiry
  const handleExtendSubmit = async (values) => {
    if (!userToExtend) return;
    setExtendLoading(true);
    const res = await dispatch(extendHmoExpiry(userToExtend.id, values));
    setExtendLoading(false);
    if (res && res.success) {
      setUserToExtend(null);
      loadHmos();
      loadAllCustomers(customerPagination.current);
    }
  };

  // Columns for HMO Providers Table
  const hmoColumns = [
    {
      title: "HMO Provider",
      dataIndex: "name",
      key: "name",
      width: 200,
      render: (text) => <span style={{ fontWeight: 600 }}>{text}</span>,
    },
    {
      title: "Allowable Checks / Week",
      dataIndex: "allowable_checks_per_week",
      key: "allowable_checks_per_week",
      width: 200,
      render: (checks) => `${checks} visits / week`,
    },
    {
      title: "Enrolled Customers",
      key: "customers_count",
      width: 200,
      render: (_, record) => (
        <span>
          <strong>{record.customers_count || 0}</strong> total
          {record.pending_count > 0 && (
            <Tag color="orange" style={{ marginLeft: "8px" }}>
              {record.pending_count} pending
            </Tag>
          )}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status) =>
        status === "active" ? (
          <Tag color="success">Active</Tag>
        ) : (
          <Tag color="default">Disabled</Tag>
        ),
    },
    {
      title: "Action",
      key: "actions",
      width: 120,
      fixed: "right",
      render: (_, record) => {
        const menu = (
          <Menu>
            <Menu.Item
              key="view"
              icon={<RiEyeLine style={{ verticalAlign: "middle" }} />}
              onClick={() => setViewCustomerHmo(record)}
            >
              View Customers ({record.customers_count || 0})
            </Menu.Item>
            <Menu.Item
              key="edit"
              icon={<RiEditLine style={{ verticalAlign: "middle" }} />}
              onClick={() => {
                setSelectedHmo(record);
                setShowHmoModal(true);
              }}
            >
              Edit Provider
            </Menu.Item>
            <Menu.Divider />
            <Menu.Item
              key="toggle"
              icon={<RiToggleLine style={{ verticalAlign: "middle" }} />}
              danger={record.status === "active"}
              onClick={() => handleToggleStatus(record)}
            >
              {record.status === "active" ? "Disable Provider" : "Enable Provider"}
            </Menu.Item>
          </Menu>
        );

        return (
          <Dropdown overlay={menu} trigger={["click"]} placement="bottomRight">
            <Button size="small">
              Actions <RiArrowDownSLine style={{ marginLeft: 2 }} />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  // Columns for Pending Approvals Table
  const pendingColumns = [
    {
      title: "Customer",
      dataIndex: "name",
      key: "name",
      width: 220,
      render: (text, record) => (
        <div>
          <Link
            to={`${dashboardRoute.admin.userProfile.link}/${record.id}`}
            style={{ fontWeight: 600, color: "#108ee9" }}
          >
            {text}
          </Link>
          <div style={{ fontSize: "12px", color: "#888" }}>{record.email}</div>
        </div>
      ),
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      width: 140,
    },
    {
      title: "HMO Provider",
      key: "hmo_name",
      width: 170,
      render: (_, record) => (
        <span style={{ fontWeight: 600 }}>{record.hmo?.name || "N/A"}</span>
      ),
    },
    {
      title: "HMO Number",
      dataIndex: "hmo_number",
      key: "hmo_number",
      width: 140,
      render: (num) => <Tag color="blue">{num}</Tag>,
    },
    {
      title: "Submitted At",
      dataIndex: "hmo_submitted_at",
      key: "hmo_submitted_at",
      width: 180,
      render: (date) => (date ? moment(date).format("DD MMM, YYYY hh:mm A") : "—"),
    },
    {
      title: "Action",
      key: "actions",
      width: 120,
      fixed: "right",
      render: (_, record) => {
        const menu = (
          <Menu>
            <Menu.Item
              key="approve"
              icon={<RiCheckLine style={{ verticalAlign: "middle", color: "#27ae60" }} />}
              onClick={() => setUserToApprove(record)}
            >
              Approve Request
            </Menu.Item>
            <Menu.Item
              key="reject"
              danger
              icon={<RiCloseLine style={{ verticalAlign: "middle" }} />}
              onClick={() => setUserToReject(record)}
            >
              Reject Request
            </Menu.Item>
            <Menu.Divider />
            <Menu.Item
              key="profile"
              icon={<RiEyeLine style={{ verticalAlign: "middle" }} />}
            >
              <Link to={`${dashboardRoute.admin.userProfile.link}/${record.id}`}>
                View Profile
              </Link>
            </Menu.Item>
          </Menu>
        );

        return (
          <Dropdown overlay={menu} trigger={["click"]} placement="bottomRight">
            <Button size="small" type="primary">
              Action <RiArrowDownSLine style={{ marginLeft: 2 }} />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  // Columns for All HMO Members Table
  const allCustomerColumns = [
    {
      title: "Customer Name",
      dataIndex: "name",
      key: "name",
      width: 200,
      render: (text, record) => (
        <div>
          <Link
            to={`${dashboardRoute.admin.userProfile.link}/${record.id}`}
            style={{ fontWeight: 600, color: "#108ee9" }}
          >
            {text}
          </Link>
          <div style={{ fontSize: "12px", color: "#888" }}>{record.email}</div>
        </div>
      ),
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      width: 140,
      render: (phone) => phone || "—",
    },
    {
      title: "HMO Provider",
      key: "hmo_name",
      width: 170,
      render: (_, record) => (
        <span style={{ fontWeight: 600 }}>{record.hmo?.name || record.hmo_name || "—"}</span>
      ),
    },
    {
      title: "HMO Number",
      dataIndex: "hmo_number",
      key: "hmo_number",
      width: 140,
      render: (num) => (num ? <Tag color="blue">{num}</Tag> : "—"),
    },
    {
      title: "Status",
      dataIndex: "hmo_status",
      key: "hmo_status",
      width: 140,
      render: (status, record) => {
        if (status === "approved") {
          const isExpired =
            record.hmo_expires_at &&
            moment(record.hmo_expires_at).isBefore(moment().startOf("day"));
          if (isExpired) {
            return <Tag color="error">Expired</Tag>;
          }
          return <Tag color="success">Approved</Tag>;
        }
        if (status === "pending") return <Tag color="warning">Pending Approval</Tag>;
        if (status === "rejected") return <Tag color="error">Rejected</Tag>;
        return <Tag>None</Tag>;
      },
    },
    {
      title: "Expiration Date",
      dataIndex: "hmo_expires_at",
      key: "hmo_expires_at",
      width: 160,
      render: (date) => {
        if (!date) return <span style={{ color: "#aaa" }}>Not set</span>;
        const isExpired = moment(date).isBefore(moment().startOf("day"));
        return (
          <span style={{ color: isExpired ? "#e74c3c" : "#27ae60", fontWeight: 500 }}>
            {moment(date).format("DD MMM, YYYY")}
            {isExpired && " (Expired)"}
          </span>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: 120,
      fixed: "right",
      render: (_, record) => {
        const menu = (
          <Menu>
            <Menu.Item
              key="extend"
              icon={<RiCalendarEventLine style={{ verticalAlign: "middle" }} />}
              onClick={() => setUserToExtend(record)}
            >
              Extend Expiry Date
            </Menu.Item>
            <Menu.Item
              key="profile"
              icon={<RiEyeLine style={{ verticalAlign: "middle" }} />}
            >
              <Link to={`${dashboardRoute.admin.userProfile.link}/${record.id}`}>
                View Profile
              </Link>
            </Menu.Item>
          </Menu>
        );

        return (
          <Dropdown overlay={menu} trigger={["click"]} placement="bottomRight">
            <Button size="small">
              Actions <RiArrowDownSLine style={{ marginLeft: 2 }} />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <Container>
      <header className="component-header">
        <div>
          <h1>HMO & Corporate Wellness Management</h1>
          <p style={{ color: "#777", margin: "4px 0 0 0", fontSize: "13px" }}>
            Configure supported HMO providers, weekly check-in limits, and manage customer approvals.
          </p>
        </div>
        <div className="header-actions">
          <Button
            type="primary"
            icon={<IoMdAdd style={{ marginRight: "6px", verticalAlign: "middle" }} />}
            onClick={() => {
              setSelectedHmo(null);
              setShowHmoModal(true);
            }}
          >
            Add HMO Provider
          </Button>
        </div>
      </header>

      {/* Summary statistics */}
      <div className="stats-banner">
        <div className="stat-card">
          <span className="stat-title">Active HMO Providers</span>
          <span className="stat-number">
            {hmos.filter((h) => h.status === "active").length}
          </span>
        </div>
        <div className="stat-card">
          <span className="stat-title">Total HMO Members</span>
          <span className="stat-number">
            {hmos.reduce((acc, h) => acc + (h.customers_count || 0), 0)}
          </span>
        </div>
        <div className="stat-card">
          <span className="stat-title">Pending Approvals</span>
          <span className="stat-number" style={{ color: pendingPagination.total > 0 ? "#e67e22" : "#222" }}>
            {pendingPagination.total}
          </span>
        </div>
      </div>

      <div className="table-card">
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="Supported HMOs" key="hmos">
            <Table
              columns={hmoColumns}
              dataSource={hmos}
              rowKey="id"
              loading={hmoLoading}
              pagination={false}
              size="middle"
              scroll={{ x: 800 }}
            />
          </TabPane>

          <TabPane
            tab={
              <span>
                Pending Approvals{" "}
                <Badge
                  count={pendingPagination.total}
                  overflowCount={999}
                  style={{ backgroundColor: "#e67e22", marginLeft: "4px" }}
                />
              </span>
            }
            key="pending"
          >
            <div style={{ marginBottom: "16px", display: "flex", justifyContent: "space-between" }}>
              <Search
                placeholder="Search pending by name, email, HMO no."
                allowClear
                onSearch={(val) => {
                  setPendingSearch(val);
                  loadPending(1, val);
                }}
                style={{ width: 320 }}
              />
              <Button onClick={() => loadPending(pendingPagination.current)}>Refresh</Button>
            </div>
            <Table
              columns={pendingColumns}
              dataSource={pendingUsers}
              rowKey="id"
              loading={pendingLoading}
              pagination={{
                ...pendingPagination,
                onChange: (page) => loadPending(page),
              }}
              size="middle"
              scroll={{ x: 950 }}
            />
          </TabPane>

          <TabPane
            tab={
              <span>
                All HMO Members{" "}
                <Badge
                  count={customerPagination.total}
                  overflowCount={9999}
                  style={{ backgroundColor: "#108ee9", marginLeft: "4px" }}
                />
              </span>
            }
            key="all_customers"
          >
            <div style={{ marginBottom: "16px", display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center" }}>
                <Search
                  placeholder="Search by name, email, phone, HMO no."
                  allowClear
                  onSearch={(val) => {
                    setCustomerSearch(val);
                    loadAllCustomers(1, val, customerHmoFilter, customerStatusFilter);
                  }}
                  style={{ width: 280 }}
                />
                <Select
                  placeholder="Filter by HMO"
                  allowClear
                  style={{ width: 180 }}
                  value={customerHmoFilter || undefined}
                  onChange={(val) => {
                    setCustomerHmoFilter(val || "");
                    loadAllCustomers(1, customerSearch, val || "", customerStatusFilter);
                  }}
                >
                  {hmos.map((h) => (
                    <Option key={h.id} value={h.id}>
                      {h.name}
                    </Option>
                  ))}
                </Select>
                <Select
                  placeholder="Filter by Status"
                  allowClear
                  style={{ width: 170 }}
                  value={customerStatusFilter || undefined}
                  onChange={(val) => {
                    setCustomerStatusFilter(val || "");
                    loadAllCustomers(1, customerSearch, customerHmoFilter, val || "");
                  }}
                >
                  <Option value="all_approved">All Approved</Option>
                  <Option value="approved">Active (Not Expired)</Option>
                  <Option value="expired">Expired</Option>
                  <Option value="pending">Pending Approval</Option>
                  <Option value="rejected">Rejected</Option>
                </Select>
              </div>
              <Button onClick={() => loadAllCustomers(customerPagination.current)}>Refresh</Button>
            </div>
            <Table
              columns={allCustomerColumns}
              dataSource={allCustomers}
              rowKey="id"
              loading={customerLoading}
              pagination={{
                ...customerPagination,
                onChange: (page) => loadAllCustomers(page),
              }}
              size="middle"
              scroll={{ x: 1000 }}
            />
          </TabPane>
        </Tabs>
      </div>

      {/* Modals */}
      <ModalCreateOrEditHmo
        visible={showHmoModal}
        hmo={selectedHmo}
        loading={hmoModalLoading}
        onClose={() => {
          setShowHmoModal(false);
          setSelectedHmo(null);
        }}
        onSubmit={handleHmoSubmit}
      />

      <ModalApproveHmo
        visible={!!userToApprove}
        user={userToApprove}
        loading={approveLoading}
        onClose={() => setUserToApprove(null)}
        onSubmit={handleApproveSubmit}
      />

      <ModalRejectHmo
        visible={!!userToReject}
        user={userToReject}
        loading={rejectLoading}
        onClose={() => setUserToReject(null)}
        onSubmit={handleRejectSubmit}
      />

      <ModalExtendExpiry
        visible={!!userToExtend}
        user={userToExtend}
        loading={extendLoading}
        onClose={() => setUserToExtend(null)}
        onSubmit={handleExtendSubmit}
      />

      <ModalHmoCustomers
        visible={!!viewCustomerHmo}
        hmo={viewCustomerHmo}
        onClose={() => setViewCustomerHmo(null)}
        onExtendExpiry={(u) => setUserToExtend(u)}
      />
    </Container>
  );
};

export default ManageHmo;
